import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RotateCcw, TrendingDown, Sparkles, Shield } from "lucide-react";
import {
  computeIndividual,
  computeDelta,
  simulateCorruptionRecovery,
} from "@/lib/simulator";
import { fmtMoney, fmtDays, fmtYears } from "@/lib/chart-utils";

function KpiDelta({ label, value, delta, unit, color }) {
  const positive = Number(delta) > 0;
  const sign = positive ? "+" : "";
  return (
    <div
      className="rounded-md border border-border bg-card p-3"
      data-testid={`whatif-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div
        className="mt-1 font-mono text-xl font-bold"
        style={{ color }}
      >
        {value}
      </div>
      {delta != null && Number.isFinite(Number(delta)) && (
        <div
          className={`mt-0.5 text-xs font-semibold ${
            positive ? "text-emerald-600" : "text-rose-600"
          }`}
        >
          {sign}
          {Number(delta).toLocaleString("fr-FR", { maximumFractionDigits: 2 })}
          {unit ? ` ${unit}` : ""} récupérés
        </div>
      )}
    </div>
  );
}

function SliderRow({
  label,
  hint,
  min,
  max,
  step,
  value,
  onChange,
  testId,
  suffix = "%",
}) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-baseline justify-between">
        <Label className="text-sm font-semibold">{label}</Label>
        <span
          className="font-mono text-sm font-bold text-brand-blue dark:text-brand-gold"
          data-testid={`${testId}-value`}
        >
          {value}
          {suffix}
        </span>
      </div>
      <Slider
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={(v) => onChange(Number(v[0]))}
      />
      {hint && (
        <div className="text-[11px] text-muted-foreground">{hint}</div>
      )}
    </div>
  );
}

export default function WhatIfSimulator({ analysis }) {
  const profiles = analysis?.results?.profiles || [];
  const [activeProfileId, setActiveProfileId] = useState(
    profiles[0]?.profile_id || null
  );
  const activeProfile = useMemo(
    () => profiles.find((p) => p.profile_id === activeProfileId) || profiles[0],
    [profiles, activeProfileId]
  );
  const baseline = activeProfile?.individual;
  const baseInputs = activeProfile?.individual?.inputs;
  const currency = analysis?.results?.summary?.currency || "UM";

  // Initial slider state from baseline (defaults survive if inputs missing)
  const [irRate, setIrRate] = useState(Number(baseInputs?.ir_rate ?? 10));
  const [socialRate, setSocialRate] = useState(
    Number(baseInputs?.social_rate ?? 7)
  );
  const [vatRate, setVatRate] = useState(Number(baseInputs?.vat_rate ?? 18));
  // Corruption recovery
  const [divertedUM, setDivertedUM] = useState(0);
  const [recoveryPct, setRecoveryPct] = useState(30);

  // Recompute simulation when profile changes
  const simulated = useMemo(() => {
    if (!baseInputs) return null;
    return computeIndividual({
      ...baseInputs,
      ir_rate: irRate,
      social_rate: socialRate,
      vat_rate: vatRate,
    });
  }, [baseInputs, irRate, socialRate, vatRate]);

  const delta = useMemo(() => computeDelta(baseline, simulated), [
    baseline,
    simulated,
  ]);

  // Reset to real values
  const reset = () => {
    if (!baseInputs) return;
    setIrRate(Number(baseInputs.ir_rate));
    setSocialRate(Number(baseInputs.social_rate));
    setVatRate(Number(baseInputs.vat_rate));
  };

  // Population active for per-citizen corruption calc
  const populationActive =
    analysis?.inputs?.population_active ||
    analysis?.results?.debt?.population_active ||
    1_000_000;
  const salaireJournalier =
    baseline?.salaire_journalier ||
    analysis?.results?.national?.salaire_journalier_moyen ||
    0;

  const corruption = useMemo(
    () =>
      simulateCorruptionRecovery({
        divertedBudgetUM: Number(divertedUM) || 0,
        populationActive,
        salaireJournalier,
        pct: recoveryPct,
      }),
    [divertedUM, populationActive, salaireJournalier, recoveryPct]
  );

  if (!baseline) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Aucun profil actif sur cette analyse.
        </CardContent>
      </Card>
    );
  }

  // Main "headline" message
  const daysRecovered = delta?.days_recovered_per_year ?? 0;
  const headline =
    daysRecovered > 0
      ? `Vous récupéreriez ${fmtDays(daysRecovered)} par an sur votre vie.`
      : daysRecovered < 0
      ? `Vous sacrifieriez ${fmtDays(Math.abs(daysRecovered))} supplémentaires par an.`
      : "Aucun changement par rapport au scénario réel.";

  return (
    <div className="space-y-5" data-testid="whatif-simulator">
      {/* Profile selector */}
      {profiles.length > 1 && (
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">
            Profil analysé
          </Label>
          <Select
            value={activeProfileId}
            onValueChange={setActiveProfileId}
          >
            <SelectTrigger
              className="w-[260px]"
              data-testid="whatif-profile-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {profiles.map((p) => (
                <SelectItem
                  key={p.profile_id}
                  value={p.profile_id}
                  data-testid={`whatif-profile-opt-${p.profile_id}`}
                >
                  {p.profile_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Headline */}
      <Card
        className="border-brand-gold/40 bg-brand-gold/5"
        data-testid="whatif-headline-card"
      >
        <CardContent className="flex items-start gap-3 p-4">
          <Sparkles className="mt-0.5 h-5 w-5 text-brand-gold" />
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              Question vitaliste
            </div>
            <div
              className="mt-1 text-lg font-bold"
              data-testid="whatif-headline"
            >
              Si l'IR passe de{" "}
              <span className="font-mono">{Number(baseInputs?.ir_rate ?? 0).toFixed(1)}%</span>{" "}
              à{" "}
              <span className="font-mono text-brand-blue dark:text-brand-gold">
                {irRate.toFixed(1)}%
              </span>{" "}
              : <span className="text-emerald-600">{headline}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Sliders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingDown className="h-5 w-5 text-brand-blue dark:text-brand-gold" />
              Paramètres fiscaux
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={reset}
              data-testid="whatif-reset-btn"
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
              Réinitialiser
            </Button>
          </CardHeader>
          <CardContent className="space-y-5">
            <SliderRow
              label="Taux d'impôt sur le revenu (IR)"
              hint={`Valeur réelle : ${Number(baseInputs?.ir_rate ?? 0).toFixed(1)}%`}
              min={0}
              max={60}
              step={0.5}
              value={irRate}
              onChange={setIrRate}
              testId="whatif-slider-ir"
            />
            <SliderRow
              label="Cotisations sociales"
              hint={`Valeur réelle : ${Number(baseInputs?.social_rate ?? 0).toFixed(1)}%`}
              min={0}
              max={40}
              step={0.5}
              value={socialRate}
              onChange={setSocialRate}
              testId="whatif-slider-social"
            />
            <SliderRow
              label="Taux de TVA"
              hint={`Valeur réelle : ${Number(baseInputs?.vat_rate ?? 0).toFixed(1)}%`}
              min={0}
              max={40}
              step={0.5}
              value={vatRate}
              onChange={setVatRate}
              testId="whatif-slider-vat"
            />
          </CardContent>
        </Card>

        {/* KPI comparison */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Impact vitaliste (temps réel)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <KpiDelta
                label="Jours/an sacrifiés"
                value={fmtDays(simulated?.jours_vie_sacrifies_par_an)}
                delta={delta?.days_recovered_per_year}
                unit="jours"
                color="#C0392B"
              />
              <KpiDelta
                label="Total prélèvements/an"
                value={fmtMoney(simulated?.total_prelevements, currency)}
                delta={delta?.money_recovered_per_year}
                unit={currency}
                color="#1A3C5E"
              />
              <KpiDelta
                label="Années carrière sacrifiées"
                value={fmtYears(simulated?.annees_vie_sur_carriere)}
                delta={delta?.years_recovered_over_career}
                unit="années"
                color="#C9A84C"
              />
              <KpiDelta
                label="% vie fiscale"
                value={
                  simulated?.ratio_vie_fiscale_pct != null
                    ? `${simulated.ratio_vie_fiscale_pct.toFixed(2)}%`
                    : "—"
                }
                delta={
                  baseline?.ratio_vie_fiscale_pct != null &&
                  simulated?.ratio_vie_fiscale_pct != null
                    ? Number(
                        (
                          baseline.ratio_vie_fiscale_pct -
                          simulated.ratio_vie_fiscale_pct
                        ).toFixed(3)
                      )
                    : null
                }
                unit="points"
                color="#0F766E"
              />
            </div>
            <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-xs text-muted-foreground">
              Comparaison à la volée entre les paramètres réels du profil et
              les valeurs que vous ajustez aux sliders. Formule canonique :{" "}
              <em>Jours_de_vie = Prélèvements / (Salaire_mensuel / 30)</em>.
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Corruption recovery */}
      <Card data-testid="whatif-corruption-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-5 w-5 text-rose-600" />
            Simulateur de restitution (lutte anti-corruption)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="diverted-um" className="text-xs">
                Budget détourné estimé par an ({currency})
              </Label>
              <Input
                id="diverted-um"
                type="number"
                min={0}
                step={1000}
                value={divertedUM}
                onChange={(e) => setDivertedUM(Number(e.target.value) || 0)}
                placeholder="Ex : 100 000 000 000"
                data-testid="whatif-diverted-input"
              />
            </div>
            <SliderRow
              label="Part restituée à la collectivité"
              hint="0 % = rien n'est récupéré · 100 % = tout est restitué."
              min={0}
              max={100}
              step={1}
              value={recoveryPct}
              onChange={setRecoveryPct}
              testId="whatif-slider-recovery"
            />
          </div>
          {corruption ? (
            <div
              className="rounded-md border border-emerald-400/40 bg-emerald-50 p-4 dark:bg-emerald-950/30"
              data-testid="whatif-corruption-result"
            >
              <div className="text-sm">
                Si <strong>{recoveryPct}%</strong> du budget détourné (
                <span className="font-mono">
                  {fmtMoney(corruption.recovered_total_um, currency)}
                </span>
                ) était restitué, chaque contribuable actif gagnerait{" "}
                <strong className="text-emerald-700 dark:text-emerald-300">
                  {fmtMoney(corruption.recovered_per_citizen_um, currency)}
                </strong>{" "}
                soit{" "}
                <strong className="text-emerald-700 dark:text-emerald-300">
                  {fmtDays(corruption.days_recovered_per_citizen)} de vie
                </strong>{" "}
                restitués par an.
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-border bg-muted/20 p-3 text-center text-xs text-muted-foreground">
              Saisissez un montant détourné pour voir la restitution vitaliste.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
